// Reset CSS
import './reset.css';

// Atoms
import Amount from './atoms/Amount';
import BusinessNameInput from './atoms/BusinessNameInput';
import Button from './atoms/Button';
import ButtonGroup from './atoms/ButtonGroup';
import Checkbox from './atoms/Checkbox';
import ParsedText from './atoms/ParsedText';
import DateTime from './atoms/DateTime';
import Disclaimer from './atoms/Disclaimer';
import Email from './atoms/Email';
import Heading from './atoms/Heading';
import HeadingCard from './atoms/HeadingCard';
import Icon from './atoms/Icon';
import Input from './atoms/Input';
import Link from './atoms/Link';
import List from './atoms/List';
import ListItem from './atoms/ListItem';
import Logo from './atoms/Logo';
import Loading from './atoms/Loading';
import Phone from './atoms/Phone';
import PhoneInput from './atoms/PhoneInput';
import PhoneLink from './atoms/PhoneLink';
import ProgressBar from './atoms/ProgressBar/ProgressBar';
import ProgressBox from './atoms/ProgressBox/ProgressBox';
import RadioButtons from './atoms/RadioButtons';
import SearchInput from './atoms/SearchInput';
import Select from './atoms/Select';
import Slider from './atoms/Slider';
import Spinner from './atoms/Spinner';
import StatusBar from './atoms/StatusBar';
import Table from './atoms/Table';
import Text from './atoms/Text';
import IFrame from './atoms/IFrame';

// Molecules
import DateInput from './molecules/DateInput';
import VerificationInput from './molecules/VerificationInput';
import Footer from './molecules/Footer';
import Graph from './molecules/Graph';
import HeadingAndList from './organisms/HeadingAndList';
import HeaderContainer from './molecules/HeaderContainer';
import Hero from './organisms/Hero';
import IncrementalInput from './molecules/IncrementalInput';
import InputField from './molecules/InputField';
import Modal from './molecules/Modal';
import StickyHeader from './molecules/StickyHeader';
import Navbar from './molecules/Navbar';
import StyledSvg from './molecules/StyledSvg';
import ToolTip from './molecules/ToolTip';
import Typeahead from './molecules/Typeahead';
import CompanyInformation from './molecules/CompanyInformation';
import QuoteItemSection from './molecules/QuoteItemSection';
import QuoteAcknowledge from './molecules/QuoteAcknowledge';
import Acknowledge from './molecules/Acknowledge';
import CardReader from './molecules/CardReader';
import AlertCard from './molecules/AlertCard';
import EligibilityBar from './molecules/EligibilityBar';
import LanguageSelect from './molecules/LanguageSelect';
import OfferDetailsCard from './molecules/OfferDetailsCard';
import OfferJourneyCard from './molecules/OfferJourneyCard';

// Organisms
import Form from './organisms/Form';
import Testimonial from './organisms/Testimonial';
import BusinessDetails from './organisms/BusinessDetails';
import QuoteContainer from './organisms/QuoteContainer';
import CompanyReference from './organisms/CompanyReference';
import OwnershipStructure from './organisms/OwnershipStructure';
import LoqateAddressLookup from './organisms/LoqateAddressLookup';

// Templates
import Container from './templates/Container';
import HorizontallyCentered from './templates/HorizontallyCentered';
import ExplorerPage from './templates/ExplorerPage';
import HunterLandingPage from './templates/HunterLandingPage';
import SaleFormPage from './templates/SaleFormPage/SaleFormPage';
import Page404 from './templates/Page404/Page404';
import ErrorPage from './templates/ErrorPage/ErrorPage';
import BoxedContent from './templates/BoxedContent';
import ErrorLayout from './templates/ErrorLayout';
import VerticalTwoColumn from './templates/VerticalTwoColumn';
import TwoOneTwoColumnRows from './templates/TwoOneTwoColumnRows';

// Scaffolds
import VerticalOneColumn from './scaffolds/VerticalOneColumn';
import Plain from './scaffolds/Plain';

// Types
// Atoms
import AmountProps from './atoms/Amount/Amount.types';
import BusinessNameInputProps from './atoms/BusinessNameInput/BusinessNameInput.types';
import ButtonProps, { ButtonColor, ButtonVariant, ButtonType } from './atoms/Button/Button.types';
import ButtonGroupProps from './atoms/ButtonGroup/ButtonGroup.types';
import CheckboxProps from './atoms/Checkbox/Checkbox.types';
import DateTimeProps from './atoms/DateTime/DateTime.types';
import DisclaimerProps from './atoms/Disclaimer/Disclaimer.types';
import EmailProps from './atoms/Email/Email.types';
import { HeadingProps, HeadingTags, HeadingTagsType } from './atoms/Heading/Heading.types';
import HeadingCardProps from './atoms/HeadingCard/HeadingCard.types';
import HiddenInputProps from './atoms/HiddenInput/HiddenInput.types';
import IconProps from './atoms/Icon/Icon.types';
import IFrameProps, { Scrolling } from './atoms/IFrame/IFrame.types';
import InputProps, { InputColor, AriaAutoCompleteValues } from './atoms/Input/Input.types';
import LinkProps from './atoms/Link/Link.types';
import ListProps, { ListLayout, ListWrap, ListType } from './atoms/List/List.types';
import ListItemProps from './atoms/ListItem/ListItem.types';
import LoadingProps from './atoms/Loading/Loading.types';
import LogoProps, { LogoImageProps } from './atoms/Logo/Logo.types';
import MaskedInputProps from './atoms/MaskedInput/MaskedInput.types';
import ParsedTextProps from './atoms/ParsedText/ParsedText.types';
import PhoneProps from './atoms/Phone/Phone.types';
import PhoneInputProps from './atoms/PhoneInput/PhoneInput.types';
import PhoneLinkProps from './atoms/PhoneLink/PhoneLink.types';
import ProgressBarProps from './atoms/ProgressBar/ProgressBar.types';
import ProgressBoxProps from './atoms/ProgressBox/ProgressBox.types';
import RadioButtonsProps, { RadioButtonOptions } from './atoms/RadioButtons/RadioButtons.types';
import SearchSelectProps, { SearchOption } from './atoms/SearchInput/SearchInput.types';
import { SelectProps, IndexableSelectOption, SelectOption } from './atoms/Select/Select.types';
import SliderProps from './atoms/Slider/Slider.types';
import SpinnerProps from './atoms/Spinner/Spinner.types';
import StatusBarProps from './atoms/StatusBar/StatusBar.types';
import TableProps from './atoms/Table/Table.types';
import TextProps from './atoms/Text/Text.types';

// Molecules
import AcknowledgeProps from './molecules/Acknowledge/Acknowledge.types';
import AlertCardProps, { AlertType, AlertTypeValues } from './molecules/AlertCard/AlertCard.types';
import CardReaderProps from './molecules/CardReader/CardReader.types';
import CompanyInformationComponentProps, {
  CompanyInformationProps,
} from './molecules/CompanyInformation/CompanyInformation.types';
import ConfirmationProps, {
  ConfirmationSections,
  ConfirmationSection,
  ConfirmationTitle,
  WildCards,
} from './molecules/ConfirmationCard/ConfirmationCard.types';
import { ContractIFrameProps } from './molecules/ContractIFrame/ContractIFrame.types';
import ContractIFrame from './molecules/ContractIFrame/ContractIFrame';
import CustomiseQuoteProps from './molecules/CustomiseQuote/CustomiseQuote.types';
import DateInputProps from './molecules/DateInput/DateInput.types';
import EligibilityBarProps from './molecules/EligibilityBar/EligibilityBar.types';
import FlipAnimationProps from './molecules/FlipAnimation/FlipAnimation.types';
import FooterProps from './molecules/Footer/Footer.types';
import GraphProps from './molecules/Graph/Graph.types';
import HeaderContainerProps from './molecules/HeaderContainer/HeaderContainer.types';
import IconButtonProps, { IconButtonVariety, IconButtonVarietyTypes } from './molecules/IconButton/IconButton.types';
import { IncrementalInputProps, IncrementStep } from './molecules/IncrementalInput/IncrementalInput.types';
import InputFieldProps from './molecules/InputField/InputField.types';
import ModalProps from './molecules/Modal/Modal.types';
import NavbarProps from './molecules/Navbar/Navbar.types';
import QuoteAcknowledgeProps from './molecules/QuoteAcknowledge/QuoteAcknowledge.types';
import QuoteItemProps from './molecules/QuoteItem/QuoteItem.types';
import QuoteItemSectionProps, { SectionProps } from './molecules/QuoteItemSection/QuoteItemSection.types';
import RemoteSearchSelectProps from './molecules/RemoteSearchSelect/RemoteSearchSelect.types';
import { ResetFormButtonProps } from './molecules/ResetFormButton/ResetFormButton.types';
import StickyHeaderProps from './molecules/StickyHeader/StickyHeader.types';
import StyledSVGProps from './molecules/StyledSvg/StyledSvg.types';
import SummaryQuoteProps from './molecules/SummaryQuote/SummaryQuote.types';
import { LanguageSelectProps } from './molecules/LanguageSelect/LanguageSelect.types';

import { ToolTipProps, AppearsFromValues, AppearsFromType } from './molecules/ToolTip/ToolTip.types';
import TypeaheadProps, { SelectParams, Option } from './molecules/Typeahead/Typeahead.types';
import {
  VerificationInputProps,
  VerificationInputPayload,
  Suffixes,
} from './molecules/VerificationInput/VerificationInput.types';
import OfferJourneyCardProps from './molecules/OfferJourneyCard/OfferJourneyCard.types';
// Organisms
import AddressLookupProps from './organisms/AddressLookup/AddressLookup.types';
import BusinessDetailsProps from './organisms/BusinessDetails/BusinessDetails.types';
import CompanyReferenceProps from './organisms/CompanyReference/CompanyReference.types';
import FormProps, { dataSchemaProps, UiSchemaProps } from './organisms/Form/Form.types';
import HeadingAndListProps from './organisms/HeadingAndList/HeadingAndList.types';
import HeroProps from './organisms/Hero/Hero.types';
import OwnershipStructureProps from './organisms/OwnershipStructure/OwnershipStructure.types';
import {
  QuoteContainerProps,
  SelectedQuoteData,
  QuoteOption,
  SummaryQuoteBlock,
  SummaryBlockProps,
} from './organisms/QuoteContainer/QuoteContainer.types';
import { QuoteListProps, QuoteSlidesProps } from './organisms/QuoteList/QuoteList.types';
import QuoteModalProps from './organisms/QuoteModal/QuoteModal.types';
import TestimonialProps from './organisms/Testimonial/Testimonial.types';
import LoqateAddressLookupProps from './organisms/LoqateAddressLookup/LoqateAddressLookup.types';

// Templates
import ContainerProps from './templates/Container/Container.types';
import ErrorPageProps from './templates/ErrorPage/ErrorPage.types';
import ExplorerPageProps from './templates/ExplorerPage/ExplorerPage.types';
import HorizontallyCenteredProps from './templates/HorizontallyCentered/HorizontallyCentered.types';
import HunterLandingPageProps from './templates/HunterLandingPage/HunterLandingPage.types';
import LandingPageProps from './templates/LandingPage/LandingPage.types';
import Page404Props from './templates/Page404/Page404.types';
import SaleFormPageProps from './templates/SaleFormPage/SaleFormPage.types';
import BoxedContentProps from './templates/BoxedContent/BoxedContent.types';
import ErrorLayoutProps from './templates/ErrorLayout/ErrorLayout.types';
import VerticalTwoColumnProps from './templates/VerticalTwoColumn/VerticalTwoColumn.types';
import TwoOneTwoColumnRowsProps from './templates/TwoOneTwoColumnRows/TwoOneTwoColumnRows.types';

// Scaffolds
import VerticalOneColumnProps from './scaffolds/VerticalOneColumn/VerticalOneColumn.types';

// Shared
import TrackingContextState, { Trackable } from './shared/TrackingContext/TrackingContext.types';
import { StyleSheetColor, StyleSheetColorType } from './shared/types/styleSheetColors.types';
import { ApplicationState, MarketingStatus, DecisionStatus } from './shared/types/ApplicationState.types';
import { JourneyType } from './shared/types/QuoteJourney.types';
import { TitlesType } from './shared/types/QuoteTitles.types';
import { ServiceCallerProps } from './shared/types/ServiceCaller.types';
import { TextColor, TextColorType } from './shared/types/textColor.types';

interface IComponentLibrary {
  [key: string]: (props: any) => JSX.Element;
}

export * from './shared/TrackingContext';
export * from './shared/LocalizationContext';

const libraryComponents: IComponentLibrary = {
  // Atoms
  Amount,
  BusinessNameInput,
  Button,
  ButtonGroup,
  Checkbox,
  Container,
  ParsedText,
  DateTime,
  Disclaimer,
  Email,
  Heading,
  HeadingCard,
  Icon,
  Input,
  Link,
  List,
  ListItem,
  Logo,
  Loading,
  Phone,
  PhoneInput,
  PhoneLink,
  ProgressBar,
  ProgressBox,
  RadioButtons,
  SearchInput,
  Select,
  Slider,
  Spinner,
  StatusBar,
  Table,
  Text,
  IFrame,

  // Molecules
  DateInput,
  VerificationInput,
  Footer,
  Graph,
  HeaderContainer,
  IncrementalInput,
  InputField,
  Modal,
  Navbar,
  StickyHeader,
  StyledSvg,
  ToolTip,
  CompanyInformation,
  QuoteAcknowledge,
  QuoteItemSection,
  Acknowledge,
  Typeahead,
  LoqateAddressLookup,
  ContractIFrame,
  EligibilityBar,
  LanguageSelect,
  OfferDetailsCard,
  OfferJourneyCard,

  // Organisms
  Form,
  Testimonial,
  Hero,
  HeadingAndList,
  BusinessDetails,
  QuoteContainer,
  CompanyReference,
  OwnershipStructure,
  CardReader,
  AlertCard,

  // Templates
  HorizontallyCentered,
  ExplorerPage,
  HunterLandingPage,
  SaleFormPage,
  Page404,
  ErrorPage,
  BoxedContent,
  ErrorLayout,

  // Scaffolds
  VerticalOneColumn,
  VerticalTwoColumn,
  Plain,
};

export default libraryComponents;

export {
  // Atoms
  Amount,
  BusinessNameInput,
  Button,
  ButtonGroup,
  Checkbox,
  Container,
  ParsedText,
  DateInput,
  DateTime,
  Disclaimer,
  Email,
  Heading,
  HeadingCard,
  Icon,
  Input,
  Link,
  List,
  ListItem,
  Logo,
  Loading,
  Phone,
  PhoneInput,
  PhoneLink,
  ProgressBar,
  ProgressBox,
  RadioButtons,
  Select,
  Slider,
  Spinner,
  StatusBar,
  Table,
  Text,
  IFrame,
  // Molecules
  Footer,
  Graph,
  HeaderContainer,
  IncrementalInput,
  InputField,
  Modal,
  StickyHeader,
  StyledSvg,
  ToolTip,
  Typeahead,
  CompanyInformation,
  QuoteAcknowledge,
  QuoteItemSection,
  Acknowledge,
  ContractIFrame,
  EligibilityBar,
  // Organisms
  Form,
  Testimonial,
  Hero,
  HeadingAndList,
  BusinessDetails,
  QuoteContainer,
  CompanyReference,
  OwnershipStructure,
  CardReader,
  AlertCard,
  OfferDetailsCard,
  OfferJourneyCard,
  // Templates
  HorizontallyCentered,
  ExplorerPage,
  HunterLandingPage,
  SaleFormPage,
  Page404,
  ErrorPage,
  BoxedContent,
  ErrorLayout,
  TwoOneTwoColumnRows,
  // Scaffolds
  VerticalOneColumn,
  VerticalTwoColumn,
  Plain,
};

// Types
export {
  // Atoms
  AmountProps,
  BusinessNameInputProps,
  ButtonProps,
  ButtonColor,
  ButtonVariant,
  ButtonType,
  ButtonGroupProps,
  CheckboxProps,
  DateTimeProps,
  DisclaimerProps,
  EmailProps,
  HeadingProps,
  HeadingTags,
  HeadingTagsType,
  HeadingCardProps,
  HiddenInputProps,
  IconProps,
  IFrameProps,
  Scrolling,
  InputProps,
  InputColor,
  AriaAutoCompleteValues,
  LinkProps,
  ListProps,
  ListLayout,
  ListWrap,
  ListType,
  ListItemProps,
  LoadingProps,
  LogoProps,
  LogoImageProps,
  MaskedInputProps,
  ParsedTextProps,
  PhoneProps,
  PhoneInputProps,
  PhoneLinkProps,
  ProgressBarProps,
  ProgressBoxProps,
  RadioButtonsProps,
  RadioButtonOptions,
  SearchSelectProps,
  SearchOption,
  SelectProps,
  IndexableSelectOption,
  SelectOption,
  SliderProps,
  SpinnerProps,
  StatusBarProps,
  TableProps,
  TextProps,
  // Molecules
  AcknowledgeProps,
  AlertCardProps,
  AlertType,
  CardReaderProps,
  CompanyInformationComponentProps,
  CompanyInformationProps,
  ConfirmationProps,
  ConfirmationSections,
  ConfirmationSection,
  ConfirmationTitle,
  WildCards,
  ContractIFrameProps,
  CustomiseQuoteProps,
  DateInputProps,
  EligibilityBarProps,
  FlipAnimationProps,
  FooterProps,
  GraphProps,
  HeaderContainerProps,
  IconButtonProps,
  IconButtonVariety,
  IconButtonVarietyTypes,
  IncrementalInputProps,
  IncrementStep,
  InputFieldProps,
  ModalProps,
  NavbarProps,
  QuoteAcknowledgeProps,
  QuoteItemProps,
  QuoteItemSectionProps,
  SectionProps,
  RemoteSearchSelectProps,
  ResetFormButtonProps,
  StickyHeaderProps,
  StyledSVGProps,
  SummaryQuoteProps,
  LanguageSelectProps,
  ToolTipProps,
  AppearsFromValues,
  AlertTypeValues,
  AppearsFromType,
  TypeaheadProps,
  SelectParams,
  OfferJourneyCardProps,
  Option,
  VerificationInputProps,
  VerificationInputPayload,
  Suffixes,
  // Organisms
  AddressLookupProps,
  BusinessDetailsProps,
  CompanyReferenceProps,
  FormProps,
  dataSchemaProps,
  UiSchemaProps,
  HeadingAndListProps,
  HeroProps,
  OwnershipStructureProps,
  QuoteContainerProps,
  SelectedQuoteData,
  QuoteOption,
  SummaryQuoteBlock,
  SummaryBlockProps,
  QuoteListProps,
  QuoteSlidesProps,
  QuoteModalProps,
  TestimonialProps,
  LoqateAddressLookupProps,
  // Templates
  ContainerProps,
  ErrorPageProps,
  ExplorerPageProps,
  HorizontallyCenteredProps,
  HunterLandingPageProps,
  LandingPageProps,
  Page404Props,
  SaleFormPageProps,
  BoxedContentProps,
  ErrorLayoutProps,
  TwoOneTwoColumnRowsProps,
  // Scaffolds
  VerticalOneColumnProps,
  VerticalTwoColumnProps,
  // Shared
  StyleSheetColor,
  StyleSheetColorType,
  TrackingContextState,
  Trackable,
  ApplicationState,
  MarketingStatus,
  DecisionStatus,
  JourneyType,
  TitlesType,
  ServiceCallerProps,
  TextColor,
  TextColorType,
};
